import { defineStore } from 'pinia';
import type { _GettersTree } from 'pinia';
import type {
    WasteViewStateType,
    WasteViewGettersType,
    WasteViewActionsType,
} from '@/types/stores/WasteViewStoreTypes';

const STORAGE_KEY = 'waste-collection-ai-view';
const ALL_READS_STORAGE_KEY = 'waste-collection-all-reads';

const readStored = (key: string): boolean => {
    try {
        return localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
};

const store = (key: string, value: boolean): void => {
    try {
        localStorage.setItem(key, value ? '1' : '0');
    } catch {
        // private mode or blocked storage: the choice lasts for this page load only
    }
};

/**
 * RFID or AI-corrected view of waste collection, shared by all its pages.
 * The axios plugin adds `view=ai` to every `waste-collection/` request while
 * it is on; the API ignores it for users who may not see the photo check.
 * `allReads` adds `reads=all` the same way (staff only, see `AllReadsSwitch`).
 */
export const useWasteViewStore = defineStore<
    'wasteViewStore',
    WasteViewStateType,
    WasteViewGettersType & _GettersTree<WasteViewStateType>,
    WasteViewActionsType
>('wasteViewStore', {
    state: (): WasteViewStateType => ({
        aiCorrected: readStored(STORAGE_KEY),
        allReads: readStored(ALL_READS_STORAGE_KEY),
    }),

    getters: {},

    actions: {
        setAiCorrected(value: boolean): void {
            this.aiCorrected = value;
            store(STORAGE_KEY, value);
        },

        setAllReads(value: boolean): void {
            this.allReads = value;
            store(ALL_READS_STORAGE_KEY, value);
        },
    },
});
